/* eslint-disable max-lines */
import type { IAvailableIcons } from '@/components/v2/ui/icon'
import type { ITrustCenterControl } from '@/types/trust-center/trustCenterControl'

export const MOCK_TRUST_CENTER_CONTROLS: ITrustCenterControl[] = [
  {
    id: '1',
    title: 'Data Protection',
    items: [
      {
        id: '1-1',
        title: 'Information classification and labeling',
        description: 'Assignment of sensitivity levels and application of labels to ensure proper data handling.',
      },
      {
        id: '1-2',
        title: 'GDPR/LOPDGDD compliance',
        description: 'Application of measures to comply with data protection legislation.',
      },
      {
        id: '1-3',
        title: 'Encryption in transit and at rest',
        description: 'Data protection through encryption both in storage and transmission.',
      },
      {
        id: '1-4',
        title: 'Rights management',
        description: 'Processes to handle requests for access, rectification, or data deletion.',
      },
      {
        id: '1-5',
        title: 'Data loss prevention',
        description: 'Implementation of controls and technologies to prevent unauthorized exfiltration of sensitive information.',
      },
      {
        id: '1-6',
        title: 'Data deletion and masking',
        description: 'Procedures to securely delete or anonymize data.',
      },
    ],
  },
  {
    id: '2',
    title: 'Identity & Access Management',
    items: [
      {
        id: '2-1',
        title: 'MFA',
        description: 'Multi-factor authentication to strengthen access security.',
      },
      {
        id: '2-2',
        title: 'Principle of least privilege',
        description: 'Granting strictly necessary permissions for each user.',
      },
      {
        id: '2-3',
        title: 'Access review',
        description: 'Periodic evaluations of permissions to detect and revoke unnecessary access.',
      },
      {
        id: '2-4',
        title: 'Privileged account management',
        description: 'Strict control of accounts with administrative permissions.',
      },
      {
        id: '2-5',
        title: 'Password management',
        description: 'Policies and practices for secure creation, storage, and renewal of passwords.',
      },
    ],
  },
  {
    id: '3',
    title: 'Networking Security',
    items: [
      {
        id: '3-1',
        title: 'Network segmentation and VLANs',
        description: 'Logical network separation to limit incident scope.',
      },
      {
        id: '3-2',
        title: 'Firewalls',
        description: 'Filtering of incoming and outgoing traffic to block unauthorized access.',
      },
      {
        id: '3-3',
        title: 'IDS/IPS',
        description: 'Network intrusion detection and prevention systems.',
      },
      {
        id: '3-4',
        title: 'Secure VPNs',
        description: 'Encrypted connections for secure remote access.',
      },
      {
        id: '3-5',
        title: 'Web filtering',
        description: 'Control and restriction of website access to prevent threats and inappropriate network use.',
      },
    ],
  },
  {
    id: '4',
    title: 'Incident Response',
    items: [
      {
        id: '4-1',
        title: 'Documented procedure',
        description: 'Detailed plan to respond to security incidents.',
      },
      {
        id: '4-2',
        title: 'Security incident reporting',
        description: 'Clear channels and procedures for employees and third parties to report incidents in a timely manner.',
      },
      {
        id: '4-3',
        title: 'Classification',
        description: 'Incident categorization according to severity.',
      },
      {
        id: '4-4',
        title: 'Authority communication',
        description: 'Notification to regulatory bodies when necessary.',
      },
      {
        id: '4-5',
        title: 'Lessons learned',
        description: 'Post-incident analysis to prevent similar incidents.',
      },
    ],
  },
  {
    id: '5',
    title: 'Information Security Management System (ISMS)',
    items: [
      {
        id: '5-1',
        title: 'Documented policies and procedures',
        description: 'Formal set of documents that define rules and processes for managing information security.',
      },
      {
        id: '5-2',
        title: 'Clear roles and responsibilities',
        description: 'Precise definition of functions and responsibilities of each person in security matters.',
      },
      {
        id: '5-3',
        title: 'Continuous risk management and compliance',
        description: 'Constant monitoring to identify and mitigate risks, ensuring regulatory compliance.',
      },
    ],
  },
  {
    id: '6',
    title: 'People',
    items: [
      {
        id: '6-1',
        title: 'Selection with background verification',
        description: 'Hiring process that includes background verification to reduce internal risks.',
      },
      {
        id: '6-2',
        title: 'Cybersecurity training',
        description: 'Regular training for employees and contractors on secure practices and internal policies.',
      },
      {
        id: '6-3',
        title: 'Confidentiality agreements',
        description: 'Contractual commitments to safeguard sensitive information.',
      },
      {
        id: '6-4',
        title: 'Onboarding, changes, and offboarding management',
        description: 'Formal procedures to manage the employment lifecycle of employees and third parties.',
      },
      {
        id: '6-5',
        title: 'Disciplinary process',
        description: 'Clear corrective measures and sanctions in case of non-compliance with security policies or inappropriate conduct.',
      },
      {
        id: '6-6',
        title: 'Remote work',
        description: 'Policies and controls to ensure secure and responsible access to organizational systems from external locations.',
      },
    ],
  },
  {
    id: '8',
    title: 'Asset Management',
    items: [
      {
        id: '8-1',
        title: 'Updated inventory',
        description: 'Complete and updated registry of physical and digital assets.',
      },
      {
        id: '8-2',
        title: 'Asset classification',
        description: 'Assignment of criticality and confidentiality levels to each asset.',
      },
      {
        id: '8-3',
        title: 'Assigned owners',
        description: 'Identification of responsible parties for each asset.',
      },
      {
        id: '8-4',
        title: 'Lifecycle and secure disposal',
        description: 'Controlled process for secure retirement and destruction of assets.',
      },
      {
        id: '8-5',
        title: 'Acceptable use of assets',
        description: 'Policies that define the permitted and responsible use of organizational assets.',
      },
      {
        id: '8-6',
        title: 'Equipment maintenance',
        description: 'Regular review and support procedures to ensure availability and proper functioning of assets.',
      },
    ],
  },
  {
    id: '9',
    title: 'Operations Security',
    items: [
      {
        id: '9-1',
        title: 'Malware protection',
        description: 'Implementation of antivirus and anti-malware solutions to prevent, detect, and eliminate malicious software.',
      },
      {
        id: '9-2',
        title: 'Patch management',
        description: 'Regular system updates to fix vulnerabilities.',
      },
      {
        id: '9-3',
        title: 'Backups',
        description: 'Periodic security copies for incident recovery.',
      },
      {
        id: '9-4',
        title: 'Monitoring and logging',
        description: 'Continuous supervision and log storage for auditing.',
      },
      {
        id: '9-5',
        title: 'Vulnerability management',
        description: 'Process to identify and fix security flaws.',
      },
      {
        id: '9-6',
        title: 'Capacity management',
        description: 'Resource monitoring and planning to ensure adequate system availability and performance.',
      },
    ],
  },
  {
    id: '10',
    title: 'Configuration Management',
    items: [
      {
        id: '10-1',
        title: 'Secure configuration by default',
        description: 'Application of security parameters from initial installation.',
      },
      {
        id: '10-2',
        title: 'Automation',
        description: 'Use of automated tools to ensure consistent configurations.',
      },
      {
        id: '10-3',
        title: 'Hardening',
        description: 'System strengthening by removing unnecessary services and configurations.',
      },
    ],
  },
  {
    id: '11',
    title: 'Secure Development',
    items: [
      {
        id: '11-1',
        title: 'SSDLC',
        description: 'Integration of security in all phases of the development lifecycle.',
      },
      {
        id: '11-2',
        title: 'Code analysis',
        description: 'Code review to detect vulnerabilities.',
      },
      {
        id: '11-3',
        title: 'Dependency management',
        description: 'Control and updating of external libraries and components.',
      },
      {
        id: '11-4',
        title: 'Security testing',
        description: 'Specific tests to verify robustness against attacks.',
      },
      {
        id: '11-5',
        title: 'Environment segregation',
        description: 'Clear differentiation between development, testing, and production environments to minimize security risks.',
      },
    ],
  },
  {
    id: '12',
    title: 'Change Management',
    items: [
      {
        id: '12-1',
        title: 'Change log',
        description: 'Detailed documentation of all changes made.',
      },
      {
        id: '12-2',
        title: 'Formal approval',
        description: 'Documented validation before implementing changes.',
      },
    ],
  },
  {
    id: '13',
    title: 'Risk Management',
    items: [
      {
        id: '13-1',
        title: 'Identification and analysis',
        description: 'Detection and study of potential risks.',
      },
      {
        id: '13-2',
        title: 'Impact assessment',
        description: 'Measurement of potential damage from each risk.',
      },
      {
        id: '13-3',
        title: 'Mitigation plans',
        description: 'Definition of actions to reduce probability or impact of risks.',
      },
      {
        id: '13-4',
        title: 'Periodic re-evaluation',
        description: 'Regular review to update the risk matrix.',
      },
    ],
  },
  {
    id: '14',
    title: 'Business Continuity',
    items: [
      {
        id: '14-1',
        title: 'BCP/DRP',
        description: 'Business continuity and disaster recovery plans.',
      },
      {
        id: '14-2',
        title: 'Periodic testing',
        description: 'Simulations and exercises to validate continuity plans.',
      },
      {
        id: '14-3',
        title: 'Redundancy',
        description: 'Duplicated infrastructure to maintain operations.',
      },
      {
        id: '14-4',
        title: 'Supplier coordination',
        description: 'Ensuring critical third parties comply with continuity plans.',
      },
    ],
  },
  {
    id: '15',
    title: 'Supply Chain',
    items: [
      {
        id: '15-1',
        title: 'Supplier accreditation',
        description: 'Formal accreditation process prior to contracting, including verification of regulatory compliance, cybersecurity maturity, and incident response capability.',
      },
      {
        id: '15-2',
        title: 'Supplier security assessment',
        description: 'Review of third-party security measures before contracting.',
      },
      {
        id: '15-3',
        title: 'Contractual clauses',
        description: 'Inclusion of security requirements in contracts.',
      },
      {
        id: '15-4',
        title: 'Monitoring',
        description: 'Continuous supervision of supplier security.',
      },
      {
        id: '15-5',
        title: 'Risk management',
        description: 'Analysis and mitigation of third-party derived risks.',
      },
    ],
  },
  {
    id: '16',
    title: 'Compliance',
    items: [
      {
        id: '16-1',
        title: 'Regulatory monitoring',
        description: 'Active surveillance of changes in laws and regulations.',
      },
      {
        id: '16-2',
        title: 'Intellectual property',
        description: 'Measures to protect patents, trademarks, copyrights, and other intellectual assets of the organization.',
      },
      {
        id: '16-3',
        title: 'Record protection',
        description: 'Policies and controls to ensure integrity, availability, and confidentiality of corporate records.',
      },
      {
        id: '16-4',
        title: 'Privacy and PII protection',
        description: 'Implementation of practices to protect personally identifiable information in accordance with applicable legislation.',
      },
    ],
  },
  {
    id: '17',
    title: 'Audit & Non-Conformities',
    items: [
      {
        id: '17-1',
        title: 'Internal audits',
        description: 'Internal review to evaluate system effectiveness.',
      },
      {
        id: '17-2',
        title: 'External audits',
        description: 'Independent reviews to verify compliance.',
      },
      {
        id: '17-3',
        title: 'Findings registry',
        description: 'Documentation of problems detected during audits.',
      },
      {
        id: '17-4',
        title: 'Corrective actions',
        description: 'Measures to resolve non-conformities.',
      },
      {
        id: '17-5',
        title: 'Closure verification',
        description: 'Confirmation that problems have been resolved.',
      },
    ],
  },
  {
    id: '18',
    title: 'Awareness',
    items: [
      {
        id: '18-1',
        title: 'Awareness campaigns',
        description: 'Actions to raise employee awareness about risks and best practices.',
      },
      {
        id: '18-2',
        title: 'Phishing simulations',
        description: 'Exercises to detect vulnerability to malicious emails.',
      },
      {
        id: '18-3',
        title: 'Educational materials',
        description: 'Guides, manuals, and resources to reinforce learning.',
      },
      {
        id: '18-4',
        title: 'Role-based training',
        description: 'Training adapted to user functions and responsibilities.',
      },
    ],
  },
  {
    id: '19',
    title: 'Communications',
    items: [
      {
        id: '19-1',
        title: 'Usage policies',
        description: 'Rules for secure use of communication channels and tools.',
      },
      {
        id: '19-2',
        title: 'Encryption',
        description: 'Protection of transmitted data through encryption.',
      },
      {
        id: '19-3',
        title: 'Secure channels',
        description: 'Use of protected means to transmit sensitive information.',
      },
      {
        id: '19-4',
        title: 'Leak management',
        description: 'Processes to contain and resolve data leakage incidents.',
      },
    ],
  },
  {
    id: '20',
    title: 'Cryptography',
    items: [
      {
        id: '20-1',
        title: 'Approved algorithms',
        description: 'Use of recognized and secure algorithms.',
      },
      {
        id: '20-2',
        title: 'Key management',
        description: 'Secure control over creation, distribution, and storage of keys.',
      },
      {
        id: '20-3',
        title: 'Periodic rotation',
        description: 'Regular renewal of keys and certificates.',
      },
      {
        id: '20-4',
        title: 'Digital signature management',
        description: 'Processes and controls for secure creation, use, and validation of digital signatures.',
      },
    ],
  },
]

// Keyed by control id in MOCK_TRUST_CENTER_CONTROLS
export const MOCK_TRUST_CENTER_CONTROL_ICONS: Record<string, IAvailableIcons> = {
  '1': 'shield-check',
  '2': 'key-outlined',
  '3': 'globe',
  '4': 'crisis-alert',
  '5': 'workflow',
  '6': 'groups-outlined',
  '7': 'package',
  '8': 'settings-outlined',
  '9': 'wrench-outlined',
  '10': 'terminal-outlined',
  '11': 'git-branch',
  '12': 'shield-alert',
  '13': 'activity',
  '14': 'storefront-outlined',
  '15': 'badge-check',
  '16': 'clipboard-list',
  '17': 'book-outlined',
  '18': 'comments',
  '19': 'lock',
}
